import React, { useState } from 'react';

const deployItems = [
  {
    title: 'Unified event store',
    description: 'Alarms, KPIs, topology, tickets and change logs from all your vendors in one place.',
  },
  {
    title: 'Alarm correlation',
    description: 'Floods grouped into incidents by topology and timing, not by volume.',
  },
  {
    title: 'Knowledge base with citations',
    description: 'Runbooks, vendor docs, past incidents and engineer notes, all citable.',
  },
  {
    title: 'Root cause and fix drafting',
    description: 'Agents test causes, rank with evidence and draft a fix for engineer approval.',
  },
];

const safeguards = [
  'Read-only by default. Engineers approve every action.',
  'Full audit trail on every correlation and recommendation.',
  'Deployed in your cloud or VPC. We never see production data.',
  'Tested against past incidents before go-live.',
];

const faqs = [
  {
    q: 'Does it replace NOC engineers?',
    a: 'No. It handles correlation and research. Engineers approve every action.',
  },
  {
    q: 'Does it work across multiple vendors?',
    a: 'Yes. That is where it helps most: joining data across all vendors.',
  },
  {
    q: 'Is it only for telecom?',
    a: 'No. Payments, utilities, logistics and IT operations all use it.',
  },
];

const OpsPage: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <section className="section-padding bg-white">
        <div className="container-max">
          <p className="text-sm text-neutral-500 mb-3">Zwift Ops</p>
          <h1 className="text-4xl md:text-5xl font-semibold text-black leading-[1.05] tracking-[-0.02em] mb-5 max-w-3xl">
            Find root cause in minutes, not hours.
          </h1>
          <p className="text-lg text-neutral-600 leading-relaxed max-w-2xl mb-6">
            Find the root cause before customers notice. Ops teams get answers in minutes, not hours.
          </p>
          <div className="flex flex-wrap gap-2">
            {['GDPR', 'NIS2', 'EU AI Act'].map((tag) => (
              <span key={tag} className="inline-flex items-center text-xs font-medium text-neutral-500 bg-neutral-100 px-2.5 py-1 rounded-full">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-neutral-50">
        <div className="container-max">
          <h2 className="text-3xl font-semibold text-black tracking-tight mb-10">What we deploy</h2>
          <div className="grid md:grid-cols-2 gap-5">
            {deployItems.map((item, i) => (
              <div key={item.title} className="bg-white rounded-2xl p-6 border border-neutral-200">
                <span className="text-xs font-medium text-neutral-300 block mb-3">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="text-lg font-semibold text-black mb-2">{item.title}</h3>
                <p className="text-neutral-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-max">
          <h2 className="text-3xl font-semibold text-black tracking-tight mb-8">Safeguards</h2>
          <ul className="space-y-4 max-w-2xl">
            {safeguards.map((s) => (
              <li key={s} className="flex items-start gap-3">
                <svg className="w-5 h-5 text-black shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-neutral-600 leading-relaxed">{s}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section-padding bg-neutral-50">
        <div className="container-max">
          <h2 className="text-3xl font-semibold text-black tracking-tight mb-4">Used in</h2>
          <div className="flex flex-wrap gap-2 mb-0">
            {['Telecom', 'Utilities', 'Payments', 'Logistics', 'IT/SaaS'].map((industry) => (
              <span key={industry} className="inline-flex items-center text-sm font-medium text-neutral-600 bg-white border border-neutral-200 px-3 py-1.5 rounded-full">
                {industry}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-max max-w-2xl">
          <h2 className="text-3xl font-semibold text-black tracking-tight mb-8">Common questions</h2>
          <div className="divide-y divide-neutral-200">
            {faqs.map((faq, i) => (
              <div key={i}>
                <button
                  className="w-full flex items-center justify-between py-5 text-left"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-medium text-black">{faq.q}</span>
                  <svg
                    className={`w-5 h-5 text-neutral-400 shrink-0 ml-4 transition-transform ${openFaq === i ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFaq === i && (
                  <p className="pb-5 text-neutral-600 leading-relaxed">{faq.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-black">
        <div className="container-max">
          <h2 className="text-3xl font-semibold text-white mb-4">
            Ready to discuss Zwift Ops?
          </h2>
          <p className="text-neutral-400 mb-8 max-w-xl">
            Book a free fit call. We will check whether Zwift Ops is the right fit and tell you honestly.
          </p>
          <a
            href="https://calendar.app.google/cnN9KoLp6WZYMX9G7"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-white hover:bg-neutral-100 text-black font-medium px-7 py-3.5 rounded-full transition-colors"
          >
            Book a fit call to discuss Zwift Ops
          </a>
        </div>
      </section>
    </>
  );
};

export default OpsPage;
